/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：TypeScript 运行时入口，负责正式研究系统的布局分流、公开页面识别、研究外壳渲染、文档标题和右侧上下文栏。
 * - English: TypeScript runtime entry for layout branching, public-page detection, research-shell rendering, document titles, and the context rail.
 *
 * 调用关系 / Call Flow
 * - 中文：由 Umi/Max 构建加载；白名单登录页必须作为公开页处理，受控研究页继续通过 route wrapper 做认证。
 * - English: Loaded by the Umi/Max build; the whitelist login page must remain public while protected research pages keep route-wrapper auth.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

// 全局初始化数据配置，用于 Layout 用户信息和权限初始化
// 更多信息见文档：https://umijs.org/docs/api/runtime-config#getinitialstate
import { initChunkRetry } from '@/utils/chunkRetry';
initChunkRetry();

import React from 'react';
import GlobalStyle from './GlobalStyle';
import ResearchWorkspaceGuide from '@/components/ResearchWorkspaceGuide';
import ResearchContextRail from '@/components/ResearchContextRail';
import {
  AUTO_RESEARCH_LANE,
  buildResearchDocumentTitle,
  buildResearchMenuData,
  resolveResearchRouteMeta,
} from '@/utils/researchNavigation';
import { isStrategySignalWorkspacePath } from '@/utils/researchWorkspaceTheme';

const isIntelligenceLoginPath = (pathname = '') => pathname === '/intelligence/login';
const isPublicExperiencePath = (pathname = '') =>
  pathname === '/' ||
  pathname === '/home' ||
  pathname.startsWith('/foundations') ||
  isIntelligenceLoginPath(pathname);
const HIDDEN_CONTEXT_RAIL_LANES = new Set([
  '/market/overview',
  '/research/symbol',
  AUTO_RESEARCH_LANE,
]);

const shouldHideResearchContextRail = (pathname = '', search = '') => {
  if (isStrategySignalWorkspacePath(pathname)) {
    return true;
  }
  const routeMeta = resolveResearchRouteMeta(pathname, search);
  return routeMeta ? HIDDEN_CONTEXT_RAIL_LANES.has(routeMeta.activeLane) : true;
};

export async function getInitialState(): Promise<{ name: string }> {
  return { name: '@umijs/max' };
}

export const request = {
  credentials: 'include' as const,
};

export const layout = () => {
  return {
    title: '分型系统',
    logo: '/branding/fractalai-mark.svg',
    siderWidth: 168,
    menu: {
      locale: false,
    },
    menuDataRender: buildResearchMenuData,
    menuRender: (props: unknown, defaultDom: React.ReactNode) =>
      isPublicExperiencePath(typeof window !== 'undefined' ? window.location.pathname : '')
        ? false
        : defaultDom,
    headerRender: (props: unknown, defaultDom: React.ReactNode) =>
      isPublicExperiencePath(typeof window !== 'undefined' ? window.location.pathname : '')
        ? false
        : defaultDom,
    onPageChange: () => {
      if (typeof document !== 'undefined') {
        document.title = buildResearchDocumentTitle(window.location.pathname, window.location.search);
      }
    },
    childrenRender: (children: React.ReactNode, props: { location?: { pathname?: string; search?: string } }) => {
      const pathname = props?.location?.pathname ||
        (typeof window !== 'undefined' ? window.location.pathname : '');
      const search = props?.location?.search ||
        (typeof window !== 'undefined' ? window.location.search : '');
      const location = props?.location || { pathname, search };
      const isPublicExperience = isPublicExperiencePath(pathname);
      const hideContextRail = shouldHideResearchContextRail(pathname, search);
      const privateExperience = React.createElement(
        React.Fragment,
        null,
        React.createElement(ResearchWorkspaceGuide, { location }),
        React.createElement(
          'div',
          {
            className: `fractal-research-shell${hideContextRail ? ' fractal-research-shell-single' : ''}`,
            style: {
              display: 'grid',
              gridTemplateColumns: hideContextRail
                ? 'minmax(0, 1fr)'
                : 'minmax(0, 1fr) minmax(248px, 272px)',
              gap: hideContextRail ? 0 : 12,
              alignItems: 'start',
            },
          },
          React.createElement(
            'div',
            { className: 'fractal-research-content', style: { minWidth: 0 } },
            children,
          ),
          hideContextRail
            ? null
            : React.createElement(
                'div',
                { className: 'fractal-research-rail' },
                React.createElement(ResearchContextRail, { location }),
              ),
        ),
      );

      return React.createElement(
        React.Fragment,
        null,
        React.createElement(GlobalStyle, null),
        isPublicExperience
          ? React.createElement(
              'div',
              { style: { padding: '24px 24px 32px' } },
              children,
            )
          : privateExperience,
      );
    },
  };
};
